import type { Task } from "@app/core";
import { cn } from "@app/ui";
import { startTransition, useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { motion } from "../../lib/motion/index.js";
import { pad } from "../../lib/format.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { demoScrollHour } from "../../lib/demo-mode.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useCalendarsStore, useInboxDragStore, useResizePreviewStore, useSettingsStore, useSidebarAnimStore, useTaskDialogStore, useThemeStore } from "../../stores/index.js";
import { washStyle } from "../block-wash.js";
import { resolvedCalendarColor } from "../entity-colors.js";
import { useBlockStyle } from "../useBlockStyle.js";
import { MobileDraftGhost } from "./MobileDraftGhost.js";
import { AllDayRow } from "./AllDayRow.js";
import { CalendarRenderIndexProvider } from "./CalendarRenderIndex.js";
import type { DayCol } from "./build-days.js";
import { DayColumn } from "./DayColumn.js";
import { isSameYMD } from "./drag.js";
import { useCalendarMutations } from "./useCalendarMutations.js";
import { useCalendarZoom } from "./useCalendarZoom.js";
import { useCreateDrag } from "./useCreateDrag.js";
import { useDayPan } from "./useDayPan.js";
import { useEdgeAutoPage } from "./useEdgeAutoPage.js";
import { useToday } from "./useNowMinute.js";
import { buildColumns, EMPTY, EMPTY_OVERFLOW } from "./build-columns.js";
import { planFlankArming } from "./flank-arming.js";
import { useCalendarTouchDrop } from "./useCalendarTouchDrop.js";
import { useTouchCalendarGestures } from "./useTouchCalendarGestures.js";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const AXIS_WIDTH_PX = 64;
/** Phone: a slim hour axis (just wide enough for a 2-digit hour, no ":00"). */
const AXIS_WIDTH_MOBILE_PX = 22;
const DAY_MS = 86_400_000;

/** One column's block data. Bundled per DATE (not per index) so the DEFERRED
 *  copy still matches the right day after a window recenter shifts indices. */
interface ColData {
  tasks: Task[];
  overflow: Array<{ task: Task; overflowMinutes: number }>;
}
const EMPTY_COL: ColData = { tasks: EMPTY, overflow: EMPTY_OVERFLOW };
const EMPTY_COL_MAP: ReadonlyMap<string, ColData> = new Map();
const EMPTY_ARMED: ReadonlySet<string> = new Set();

interface Props {
  /** WIDE, stable day window (visible days + buffer columns each side). */
  days: DayCol[];
  windowStart: Date;
  visibleStart: Date;
  visibleCount: number;
  showHeader?: boolean;
  /** Plain wheel/drag on the header shifts the anchor by ±days. */
  onDayShift?: (delta: number) => void;
  /** Shift+wheel on the header changes how many days are displayed. */
  onDayCountChange?: (delta: number) => void;
}

export function TimeAxisGrid({ days, windowStart, visibleStart, visibleCount, showHeader = true, onDayShift, onDayCountChange }: Props) {
  const today = useToday();
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const { onTaskResize, onTaskResizeTop, handleDrop, handleAllDayDrop } = useCalendarMutations();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Live height of the scroll viewport: feeds the zoom hook's dynamic floor
  // (`viewH / 24`) so the 24h track can never be shorter than the page — 23:59
  // stays pinned to the bottom, and zoom-out stops there. Tracks window
  // resizes (and all-day-row growth) via the ResizeObserver.
  const [viewH, setViewH] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { hourHeight, onZoomWheel, zoomTo } = useCalendarZoom(viewH / 24);
  const dayHeight = HOURS.length * hourHeight;
  const maxScrollTop = Math.max(0, dayHeight - viewH);
  // The offset THIS component last wrote (post-clamp, as the browser resolved
  // it). Every write the grid makes itself goes through `writeTimedScroll`, so a
  // scroll event that does NOT match came from somewhere else — see
  // `scrollOwnedByUser` below.
  const writtenScrollRef = useRef(-1);
  const writeTimedScroll = useCallback((top: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = top;
    writtenScrollRef.current = el.scrollTop;
  }, []);
  const clampTimedScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || viewH <= 0) return;
    if (el.scrollTop > maxScrollTop) writeTimedScroll(maxScrollTop);
  }, [maxScrollTop, viewH, writeTimedScroll]);
  // Live per-column width (px) of the header track, so phone day-titles can wrap
  // to two lines ("22" over "Mon") only when a single "22/Mon" line won't fit.
  const [colPx, setColPx] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const allDayRef = useRef<HTMLDivElement | null>(null);
  // The visible timed-grid area (excludes the time axis) — geometry source for
  // desktop drag edge-paging.
  const gridAreaRef = useRef<HTMLDivElement | null>(null);
  const mobile = useMobileUx();
  const axisWidth = mobile ? AXIS_WIDTH_MOBILE_PX : AXIS_WIDTH_PX;
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const headerRef = useRef<HTMLDivElement | null>(null);
  const headerDragRef = useRef<{ startX: number; lastDelta: number } | null>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setColPx(el.clientWidth / Math.max(1, visibleCount));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visibleCount, showHeader]);
  // "22/Mon" is ~40px wide at text-sm; at larger app text sizes it needs more
  // room, so stack the date over the weekday instead of clipping it.
  const twoLineHeaders = mobile && colPx > 0 && colPx < 64;

  // ── The ONE px measurement of the track's horizontal geometry.
  //
  // All horizontal geometry is %-based on purpose (see docs/calendar-view.md) —
  // this is not a second source of truth for layout, it is the px number the
  // nest/content fit needs, and every column has the same one: each is
  // `100/days.length`% of a track that is `days.length/visibleCount`× the grid
  // area, so a column is exactly `gridArea / visibleCount` wide.
  //
  // Measured here rather than per column so the columns' first block-bearing
  // render already has the real width, and so a window resize runs ONE
  // ResizeObserver instead of one per mounted column. Measures the grid AREA,
  // not the header, because `showHeader` is false on some routes.
  const [gridAreaPx, setGridAreaPx] = useState(0);
  useEffect(() => {
    const el = gridAreaRef.current;
    if (!el) return;
    const measure = () => {
      // Skip while a sidebar gesture animates the viewport width per frame: the
      // %-sized columns scale natively and the blocks are left/right-anchored, so
      // only the px nest numbers go briefly stale. One settle re-measure follows.
      if (useSidebarAnimStore.getState().active) return;
      setGridAreaPx(el.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Settle once when the sidebar gesture ends. TRANSIENT subscribe (not the
  // reactive hook) so a gesture START doesn't re-render the grid.
  useEffect(() => useSidebarAnimStore.subscribe((s, prev) => {
    if (!s.active && prev.active && gridAreaRef.current) setGridAreaPx(gridAreaRef.current.clientWidth);
  }), []);
  const colWidthPxRaw = gridAreaPx > 0 ? gridAreaPx / Math.max(1, visibleCount) : 0;
  // Coalesce bursts of width ticks (a window resize) so the columns' nest-layout
  // recompute doesn't run every frame — but NEVER defer the first real width.
  // The columns mount one render before the data arrives, so a plain
  // `useDeferredValue` hands the block-MOUNTING render the placeholder width and
  // then re-renders every block once the deferred value catches up: measured, 393
  // of 1,675 block renders on a dense week boot were exactly that.
  const colWidthDeferred = useDeferredValue(colWidthPxRaw);
  const colWidthPx = colWidthDeferred > 0 ? colWidthDeferred : colWidthPxRaw;

  const { x, drag: panDrag } = useDayPan(windowStart, visibleStart, days.length);
  // %-based geometry (of the viewport / of the track) — the browser scales the
  // grid natively when the viewport width animates (sidebar collapse/expand,
  // window resize); no measuring, no per-frame React work.
  const trackWidth = `${(days.length / Math.max(1, visibleCount)) * 100}%`;
  const colWidth = `${100 / days.length}%`;
  const mobileColumnStyle = useMemo<CSSProperties | undefined>(
    () => mobile
      ? { width: colWidth, contentVisibility: "auto", containIntrinsicSize: `1px ${dayHeight}px` }
      : undefined,
    [colWidth, dayHeight, mobile],
  );

  const { startCreate, previewFor, clearPending } = useCreateDrag({
    gridRef: trackRef,
    axisWidthPx: 0,
    dayCount: days.length,
    hourHeight,
    onCreate: (dayIdx, startMin, total) => {
      const date = new Date(days[dayIdx]!.date);
      date.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
      // The mobile create sheet (driven by openCreate) shows the draggable
      // MobileDraftGhost on this track while open.
      useTaskDialogStore.getState().openCreate(date, total || undefined);
    },
  });

  // Mobile touch gestures: 1-finger horizontal pan = day shift, pinch = zoom /
  // day count, long-press a block/chip + drag = move (ghost switches between
  // the timed track and the all-day band under the finger).
  const { touchGhost } = useTouchCalendarGestures({
    enabled: mobile,
    scrollRef,
    allDayRef,
    trackRef,
    days,
    visibleCount,
    hourHeight,
    zoomTo,
    pan: panDrag,
    onDayShift,
    onDayCountChange,
    onDrop: (day, min, id) => void handleDrop(day, min, id),
    onAllDayDrop: (day, id, duration) => void handleAllDayDrop(day, id, duration),
    onTapTask: openEdit,
  });

  // ── The anchor hour, and who owns the timed scroll.
  //
  // Landing the grid at its anchor hour must cost ONE raster. The blocks are the
  // expensive thing to paint, and any scroll AFTER they raster re-rasters the
  // whole 3.5-viewport-wide track: measured on the dense week, a 38px correction
  // cost 111 ms of main-thread `ScrollLayer`.
  //
  // Two things fight the anchor during boot. The all-day row's height is a
  // function of the DATA, so it jumps from its one-row placeholder to the real
  // stack when tasks hydrate — and the compensation below (which exists to hold
  // the grid still across DAY SHIFTS) then dragged the scroller by that delta,
  // landing the boot ~6 hours past the anchor AND out of range, so the clamp
  // corrected it after the raster. So until the user owns the scroll, an all-day
  // resize RE-ASSERTS the anchor instead of compensating; the ResizeObserver runs
  // after layout and before paint, which is exactly where that write is free.
  //
  // Derived from live DOM (`scrollHeight` is the 24h track) rather than from
  // `hourHeight`/`viewH` state, so it is correct in a layout effect on the very
  // first commit, before either has been measured.
  //
  // Ownership transfers on the first scroll the grid did not write itself (a
  // wheel, a touch pan, the overlay scrollbar's thumb — which lives on
  // `document.body`, not inside the scroller — or a `scrollIntoView` from a
  // calendar focus jump), and on the first day shift, which is the case the
  // compensation exists for.
  const scrollOwnedByUser = useRef(false);
  const applyAnchorScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Ghost demos anchor on the script's fixtures (not the wall clock) so all
    // demo blocks are on screen from the start and the run never scrolls.
    const anchorHour = demoScrollHour() ?? Math.max(0, new Date().getHours() - 2);
    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    writeTimedScroll(Math.min((anchorHour / HOURS.length) * el.scrollHeight, max));
  }, [writeTimedScroll]);

  // Layout effect, not a passive one: the anchor has to be in place before the
  // first paint, or the boot rasters at offset 0 and then again at the anchor.
  useLayoutEffect(() => {
    applyAnchorScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTimedScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (Math.abs(el.scrollTop - writtenScrollRef.current) > 1) scrollOwnedByUser.current = true;
    clampTimedScroll();
  }, [clampTimedScroll]);

  // A LAYOUT effect, not a passive one: a day shift resizes the all-day section,
  // and its ResizeObserver callback runs before passive effects — so ownership
  // has to have transferred by then or the first shift would re-anchor the grid
  // instead of holding it still.
  const mountedVisibleStart = useRef(visibleStart.getTime());
  useLayoutEffect(() => {
    if (visibleStart.getTime() !== mountedVisibleStart.current) scrollOwnedByUser.current = true;
  }, [visibleStart]);

  useEffect(() => {
    clampTimedScroll();
  }, [clampTimedScroll]);

  // The all-day section auto-sizes to the visible columns. On BOTH platforms,
  // hold the timed grid visually STILL when it grows/shrinks — instead of
  // letting the section push the grid up/down, compensate the scroll by the
  // height delta so the section just reveals/covers more of the (otherwise
  // fixed) grid. The RO callback runs after layout but before paint, so the
  // correction lands in the same frame as the height change (no jitter).
  useEffect(() => {
    const el = allDayRef.current;
    const scroller = scrollRef.current;
    if (!el || !scroller) return;
    let prev = el.getBoundingClientRect().height;
    const ro = new ResizeObserver(() => {
      const h = el.getBoundingClientRect().height;
      const delta = h - prev;
      if (delta === 0) return;
      prev = h;
      // Boot: the section is growing to fit data that only just arrived, so
      // there is no scroll position to preserve — land the anchor hour instead.
      // Compensating here walked the boot out of range and paid for it in a
      // post-raster clamp (see the anchor notes above).
      if (scrollOwnedByUser.current) writeTimedScroll(scroller.scrollTop + delta);
      else applyAnchorScroll();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [applyAnchorScroll, writeTimedScroll]);

  // Drag-to-create keeps its selection block pinned while the create dialog is
  // open; drop it once the dialog closes (saved or cancelled).
  const dialogOpen = useTaskDialogStore((s) => s.open);
  useEffect(() => {
    if (!dialogOpen) clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  // Mobile: register the inbox→calendar touch-drop resolver (an inbox row's
  // release calls `calendarTouchDrop` → this maps the point to a day+slot).
  useCalendarTouchDrop({
    enabled: mobile,
    scrollRef,
    allDayRef,
    trackRef,
    days,
    visibleCount,
    hourHeight,
    onDayShift,
    onDrop: handleDrop,
    onAllDayDrop: handleAllDayDrop,
  });

  // Desktop: native HTML5 block/inbox drag near the grid's left/right edge pages
  // the day window (the touch move-loop handles the same on mobile). Inert on
  // mobile — no native dragover fires there.
  useEdgeAutoPage(gridAreaRef, onDayShift);

  function startHeaderDrag(e: React.MouseEvent) {
    if (!onDayShift || e.button !== 0) return;
    e.preventDefault();
    headerDragRef.current = { startX: e.clientX, lastDelta: 0 };
    function onMove(ev: globalThis.MouseEvent) {
      if (!headerDragRef.current || !headerRef.current) return;
      const colW = headerRef.current.getBoundingClientRect().width / Math.max(1, visibleCount);
      const rawDelta = Math.round((headerDragRef.current.startX - ev.clientX) / colW);
      const diff = rawDelta - headerDragRef.current.lastDelta;
      if (diff !== 0) { headerDragRef.current.lastDelta = rawDelta; onDayShift!(diff); }
    }
    function onUp() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      headerDragRef.current = null;
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function onHeaderWheel(e: React.WheelEvent) {
    const delta = e.deltaX !== 0 ? Math.sign(e.deltaX) : Math.sign(e.deltaY);
    if (delta === 0) return;
    if (e.shiftKey && onDayCountChange) { e.preventDefault(); onDayCountChange(delta); return; }
    if (onDayShift) { e.preventDefault(); onDayShift(delta); }
  }

  // Derive per-column data from the (stable) window so a step doesn't rebuild it
  // — keeps the memoized DayColumns from re-rendering while only the track pans.
  const { timedByCol, overflowByCol, allDayTaskMap } = useMemo(() => buildColumns(days), [days]);

  const firstVisibleCol = Math.round((visibleStart.getTime() - windowStart.getTime()) / DAY_MS);

  // ── Visible days commit synchronously; the pan track's off-screen FLANKS
  // render at transition priority. The camera-pan track deliberately mounts a
  // buffer page each side (see useDayWindow), so on a dense calendar one visible
  // week costs ~3.5× its own blocks. Splitting the commit keeps the geometry
  // (every column still exists at its %-width, nothing is capped or hidden) and
  // only moves the FLANKS' block trees off the urgent render: the visible week
  // paints first, the buffer fills in behind it. See docs/calendar-view.md.
  //
  // Keyed by date, not index, because a recenter renumbers the columns while the
  // deferred copy is still the pre-recenter one — an index lookup would hand a
  // flank column the wrong day's blocks for a frame.
  const colKeys = useMemo(() => days.map((d) => d.date.toISOString()), [days]);
  const colDataByDate = useMemo(() => {
    const map = new Map<string, ColData>();
    for (let i = 0; i < colKeys.length; i++) {
      map.set(colKeys[i]!, { tasks: timedByCol[i] ?? EMPTY, overflow: overflowByCol[i] ?? EMPTY_OVERFLOW });
    }
    return map;
  }, [colKeys, timedByCol, overflowByCol]);
  // Initial value EMPTY: on a cold start the first data-bearing commit mounts the
  // visible days ALONE, and the buffer arrives in the follow-up transition.
  const flankData = useDeferredValue<ReadonlyMap<string, ColData>>(colDataByDate, EMPTY_COL_MAP);

  // A transition RENDER is time-sliced, but its COMMIT is not — arming all ~18
  // buffer columns at once is still one multi-hundred-ms frame on a dense
  // calendar. So the buffer arms outward from the visible window a step at a
  // time, and only up to a block budget: see `flank-arming.ts` for why that is
  // by block count and not by column count.
  const costAt = useCallback(
    (i: number) => (timedByCol[i]?.length ?? 0) + (overflowByCol[i]?.length ?? 0),
    [timedByCol, overflowByCol],
  );
  const [armedFlanks, setArmedFlanks] = useState<ReadonlySet<string>>(EMPTY_ARMED);
  useEffect(() => {
    const next = planFlankArming({ keys: colKeys, firstVisibleCol, visibleCount, costAt, armed: armedFlanks });
    if (!next) return;
    // Double rAF: let the previous step actually paint before queueing the next.
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => startTransition(() => setArmedFlanks(next)));
    });
    return () => cancelAnimationFrame(raf);
  }, [colKeys, firstVisibleCol, visibleCount, costAt, armedFlanks]);

  // Drag-to-create preview wash = a new event in the default calendar (mirrors
  // tasks.store.add()'s backfill). Column-independent, so compute it ONCE here
  // (stable ref) instead of per-column — the columns no longer subscribe to the
  // calendars/settings/block-style stores N times just to paint this preview.
  const { tint, gradient } = useBlockStyle();
  const calendars = useCalendarsStore((s) => s.calendars);
  const defaultCalId = useSettingsStore((s) => s.defaultCalendarId);
  const theme = useThemeStore((s) => s.config);
  const defaultCalColor = resolvedCalendarColor(theme, calendars, defaultCalId ?? calendars[0]?.id ?? null);
  const createWash = useMemo(
    () => washStyle({ priority: "none", custom_color: null, is_event: true }, defaultCalColor, tint, gradient),
    [defaultCalColor, tint, gradient],
  );

  // Maps a cursor point to the grid instant under it (day column + time of day),
  // entirely from live DOM rects so the closure never goes stale. Powers cross-day
  // bottom-edge resize: the handler in each block reports the dragged end instant,
  // letting a task grow to span several columns. Clamps to the nearest column when
  // the cursor is past the grid edge.
  const pointToInstant = useCallback((clientX: number, clientY: number): Date | null => {
    const area = gridAreaRef.current;
    if (!area) return null;
    const cols = area.querySelectorAll<HTMLElement>("[data-col-date]");
    if (cols.length === 0) return null;
    let chosen: HTMLElement | null = null;
    let bestDist = Infinity;
    for (const col of cols) {
      const r = col.getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right) { chosen = col; bestDist = 0; break; }
      const dist = clientX < r.left ? r.left - clientX : clientX - r.right;
      if (dist < bestDist) { bestDist = dist; chosen = col; }
    }
    if (!chosen?.dataset.colDate) return null;
    const r = chosen.getBoundingClientRect();
    const minute = Math.max(0, Math.min(1440, ((clientY - r.top) / Math.max(1, r.height)) * 1440));
    const out = new Date(chosen.dataset.colDate);
    out.setHours(0, 0, 0, 0);
    out.setMinutes(Math.round(minute));
    return out;
  }, []);

  // Live cross-day resize ghost: a washed rect per spanned day, painted over the
  // (dimmed) source block. Re-renders this grid per drag tick; the memoized
  // columns are untouched.
  const resizePreview = useResizePreviewStore((s) => s.preview);
  const inboxPreview = useInboxDragStore((s) => s.preview);

  const headerInteractive = !!onDayShift || !!onDayCountChange;
  const allDayGhost = touchGhost?.kind === "all-day" ? touchGhost : inboxPreview?.kind === "all-day" ? inboxPreview : null;
  const timedGhost = touchGhost?.kind === "timed" ? touchGhost : inboxPreview?.kind === "timed" ? inboxPreview : null;

  return (
    // `h-full` (not a viewport calc): the card always fills the route-provided
    // height — down to the window bottom — and tracks window resizes live. When
    // the 24h track is shorter than the window (zoomed out) the card keeps its
    // full height with blank space below the last hour.
    <CalendarRenderIndexProvider>
    <div className={cn("flex h-full flex-col overflow-hidden bg-card", !mobile && "rounded-md border")}>
      {showHeader && (
        <div className="flex shrink-0 border-b">
          <div style={{ width: axisWidth }} className="shrink-0" />
          <div
            ref={headerRef}
            className={cn("relative flex-1 overflow-hidden", headerInteractive && "cursor-grab active:cursor-grabbing")}
            onMouseDown={startHeaderDrag}
            onWheel={onHeaderWheel}
          >
            <motion.div className="flex" style={{ x, width: trackWidth, willChange: "transform" }}>
              {days.map((d) => {
                const isToday = isSameYMD(d.date, today);
                const weekday = d.date.toLocaleDateString(locale, { weekday: "short" });
                // Mobile: "12/Fri" on one line to free vertical space; falls back
                // to a stacked "12" / "Fri" two-line layout when the column is too
                // narrow for the single line to fit.
                if (mobile) {
                  return (
                    <div
                      key={d.date.toISOString()}
                      style={{ width: colWidth }}
                      className={cn(
                        "flex-none select-none px-1 py-1 text-center leading-tight",
                        twoLineHeaders && "flex flex-col items-center",
                        isToday && "bg-primary/5",
                      )}
                    >
                      <span className={cn("text-sm font-semibold", isToday && "text-primary")}>{d.date.getDate()}</span>
                      <span className={cn("text-sm text-muted-foreground", isToday && "text-primary/80")}>
                        {twoLineHeaders ? weekday : `/${weekday}`}
                      </span>
                    </div>
                  );
                }
                return (
                  <div key={d.date.toISOString()} style={{ width: colWidth }} className={cn("flex-none select-none px-2 py-2 text-center text-sm", isToday && "bg-primary/5")}>
                    <div className={cn("text-xs uppercase tracking-wide text-muted-foreground", isToday && "text-primary")}>{weekday}</div>
                    <div className={cn("text-lg font-semibold", isToday && "text-primary")}>{d.date.getDate()}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      )}

      <div ref={allDayRef} className="relative shrink-0">
        <AllDayRow
          days={days}
          allDayTaskMap={allDayTaskMap}
          axisWidthPx={axisWidth}
          colWidth={colWidth}
          trackWidth={trackWidth}
          x={x}
          firstVisibleCol={firstVisibleCol}
          visibleCount={visibleCount}
          compact={mobile}
        />
        {allDayGhost && (
          <div
            className="pointer-events-none absolute z-50 flex h-5 items-center overflow-hidden rounded border-2 border-primary bg-card px-1.5 text-[calc(11px*var(--app-text-scale))] font-medium text-foreground shadow-xl ring-2 ring-primary/50"
            style={{
              left: allDayGhost.leftPx,
              top: allDayGhost.topPx,
              width: Math.max(24, allDayGhost.widthPx * 0.95),
            }}
          >
            <span className="truncate">{allDayGhost.title || "Task"}</span>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="no-scrollbar min-h-0 grow touch-pan-y overflow-y-auto overflow-x-hidden overscroll-contain" onScroll={onTimedScroll}>
        <div className="flex overflow-hidden" style={{ height: dayHeight }}>
          {/* `data-time-axis`: Shift+wheel zoom is attached HERE only (so normal
              body scrolling stays off React's wheel path), which also makes this
              the one element a zoom benchmark can legitimately aim at. */}
          <div data-time-axis className="shrink-0 border-r" style={{ width: axisWidth, height: dayHeight }} onWheel={onZoomWheel}>
            {HOURS.map((hour) => (
              <div key={hour} className={cn("border-b text-right text-[calc(10px*var(--app-text-scale))] text-muted-foreground", mobile ? "pr-1" : "px-1")} style={{ height: hourHeight }}>
                {hour === 0 ? "" : mobile ? pad(hour) : `${pad(hour)}:00`}
              </div>
            ))}
          </div>
          <div ref={gridAreaRef} className="relative flex-1 overflow-hidden">
            <motion.div ref={trackRef} className="flex overflow-hidden" style={{ x, width: trackWidth, height: dayHeight, willChange: "transform" }} onMouseDown={startCreate}>
              {/* Long-press touch move: live ghost of the grabbed block. */}
              {timedGhost && (
                <div
                  className="pointer-events-none absolute z-40 overflow-hidden rounded border-2 border-primary bg-card px-1.5 py-0.5 text-foreground shadow-xl ring-2 ring-primary/50"
                  style={{
                    left: `${(timedGhost.col / days.length) * 100}%`,
                    width: colWidth,
                    top: (timedGhost.topMin / 60) * hourHeight,
                    height: Math.max((timedGhost.durationMin / 60) * hourHeight, 12),
                  }}
                >
                  <div className="break-words text-xs font-medium leading-tight">{timedGhost.title}</div>
                  <div className="break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">
                    {tf.range(timedGhost.topMin, timedGhost.topMin + timedGhost.durationMin)}
                  </div>
                </div>
              )}
              {mobile && <MobileDraftGhost days={days} hourHeight={hourHeight} trackRef={trackRef} />}
              {resizePreview && (() => {
                const total = Math.round((resizePreview.endMs - resizePreview.startMs) / 60_000);
                const label = `${Math.floor(total / 60)}h${total % 60 ? ` ${total % 60}m` : ""}`;
                return days.map((d, i) => {
                  const dayStartMs = d.date.getTime();
                  const segStart = Math.max(resizePreview.startMs, dayStartMs);
                  const segEnd = Math.min(resizePreview.endMs, dayStartMs + 86_400_000);
                  if (segEnd <= segStart) return null;
                  const topMin = (segStart - dayStartMs) / 60_000;
                  const isFirst = segStart === resizePreview.startMs;
                  return (
                    <div
                      key={`resize-${i}`}
                      className={cn(
                        "pointer-events-none absolute z-40 overflow-hidden rounded border px-1.5 py-0.5 shadow-lg",
                        resizePreview.visual.washed ? "task-wash" : resizePreview.visual.toneClass,
                        resizePreview.visual.task.virtual && "border-dashed",
                      )}
                      style={{
                        ...resizePreview.visual.vars,
                        // Inline `position` beats `.task-wash { position: relative }`
                        // (unlayered, so it wins over the Tailwind `absolute` utility);
                        // without this the ghost falls into flex flow and collapses.
                        position: "absolute",
                        left: `${(i / days.length) * 100}%`,
                        width: colWidth,
                        top: (topMin / 60) * hourHeight,
                        height: Math.max(((segEnd - segStart) / 60_000 / 60) * hourHeight, 12),
                      }}
                    >
                      {isFirst && (
                        <>
                          <div className="break-words text-xs font-medium leading-tight">{resizePreview.visual.task.title || "Untitled"}</div>
                          <div className="break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">{label}</div>
                        </>
                      )}
                    </div>
                  );
                });
              })()}
              {days.map((d, i) => {
                const iso = colKeys[i]!;
                const onScreen = i >= firstVisibleCol && i < firstVisibleCol + visibleCount;
                const col = onScreen
                  ? colDataByDate.get(iso) ?? EMPTY_COL
                  : armedFlanks.has(iso) ? flankData.get(iso) ?? EMPTY_COL : EMPTY_COL;
                return (
                <div key={iso} data-col-date={iso} style={mobileColumnStyle ?? { width: colWidth }} className="flex-none">
                  <DayColumn
                    day={d.date}
                    tasks={col.tasks}
                    overflowTasks={col.overflow}
                    isToday={isSameYMD(d.date, today)}
                    isLast={i === days.length - 1}
                    hourHeight={hourHeight}
                    createPreview={previewFor(i)}
                    createWash={createWash}
                    onDrop={(min, id) => void handleDrop(d.date, min, id)}
                    onTaskClick={openEdit}
                    onTaskResize={onTaskResize}
                    onTaskResizeTop={onTaskResizeTop}
                    pointToInstant={pointToInstant}
                    colWidthPx={colWidthPx}
                  />
                </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
    </CalendarRenderIndexProvider>
  );
}
